import type { ReactNode } from 'react';
import type { Key } from 'react-aria';
import type { SelectProps } from '@launchpad-ui/components';
import {
  Button,
  Label,
  ListBox,
  Popover,
  Select as LaunchpadSelect,
  SelectValue,
  Text,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { useFieldContext } from '../../contexts';

import { FormFieldError } from './FormFieldError';

import styles from './Select.module.css';

export type Props<T extends object> = Omit<SelectProps<T>, 'children'> & {
  label?: ReactNode;
  description?: ReactNode;
  items?: Iterable<T>;
  children: React.ReactNode | ((item: T) => React.ReactNode);
  listFooter?: ReactNode;
};

export const Select = <T extends object>(props: Props<T>) => {
  const field = useFieldContext<string>();
  const isInvalid = field.state.meta.isTouched && field.state.meta.isDirty && field.state.meta.errors.length > 0;

  return (
    <>
      <LaunchpadSelect
        isInvalid={isInvalid}
        onSelectionChange={(key: Key | null) => {
          field.handleChange(key as string);
        }}
        onBlur={() => {
          field.handleBlur();
        }}
        selectedKey={field.state.value ?? props.defaultSelectedKey}
        {...props}
      >
        {props.label !== undefined && <Label>{props.label}</Label>}
        <Button>
          <SelectValue />
          <Icon name="chevron-down" size="small" />
        </Button>
        {props.description && <Text slot="description">{props.description}</Text>}
        <FormFieldError meta={field.state.meta} />
        <Popover className={styles.popover}>
          <ListBox items={props.items}>{props.children}</ListBox>
          {props.listFooter && <div className={styles.listFooter}>{props.listFooter}</div>}
        </Popover>
      </LaunchpadSelect>
    </>
  );
};
